import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import type {
  Account,
  BrokerageProposal,
  BrokerageProposedLine,
  ParsedStatement,
  StatementReconPreview,
} from '@accounting/shared';
import { AccountPicker } from '../accounts/AccountPicker';
import { BrokeragePostingReview } from './BrokeragePostingReview';
import {
  useBrokerageProposal,
  useParseStatement,
  usePostBrokerageStatement,
  useStartReconFromStatement,
  useStatementPreview,
} from '../../hooks/useReconciliation';
import { useBankAccounts } from '../../hooks/useBank';
import { ApiError } from '../../lib/api';
import { formatAccountingDate, formatCurrency } from '../../lib/formatters';

/**
 * Start a reconciliation from the statement itself.
 *
 * The old way round was to key in the closing date and the ending balance, then
 * hunt for the entries. Here the document is the input: the AI reads the header
 * and the rows, the books are searched for what those rows describe, and the
 * owner arrives at a worksheet that is already mostly ticked.
 *
 * Nothing is written until the final button. Everything the AI proposes stays
 * editable up to that point, because a misread closing balance that reaches a
 * completed reconciliation can only be undone through the hash chain.
 */

const ACCEPTED = '.pdf,.csv,application/pdf,text/csv';

function apiErrorMessage(err: unknown): string | undefined {
  if (err instanceof ApiError) {
    return (err.body as { error?: string } | null)?.error ?? 'Request failed';
  }
  return err instanceof Error ? err.message : undefined;
}

function readAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Could not read the file.'));
    reader.onload = () => {
      const result = String(reader.result ?? '');
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.readAsDataURL(file);
  });
}

function mimeOf(file: File): string {
  if (file.type) return file.type;
  return file.name.toLowerCase().endsWith('.csv') ? 'text/csv' : 'application/pdf';
}

/** Trailing digits of an account number, for comparing a statement to a bank row. */
function maskDigits(value: string | null | undefined): string {
  return (value ?? '').replace(/\D/g, '').slice(-4);
}

/**
 * Which GL account this statement belongs to, read off the statement itself.
 *
 * Banks print the account number and every bank row stores its mask, so the
 * document can say which account it is. Returns null unless EXACTLY one bank
 * account matches — two accounts ending 2329 is a coin toss, and a confident
 * wrong answer here is worse than no answer.
 */
function matchGlAccountFromStatement(
  header: { accountMask: string | null; institutionName: string | null },
  fileName: string | null,
  bankAccounts: { accountId: string; accountMask: string | null; accountName: string }[],
): { glAccountId: string; accountName: string } | null {
  // The parsed header first; failing that the filename, which for these
  // statements carries the full account number (…_000180102329.pdf).
  for (const digits of [maskDigits(header.accountMask), maskDigits(fileName)]) {
    if (digits.length < 4) continue;
    const hits = bankAccounts.filter((b) => maskDigits(b.accountMask) === digits);
    if (hits.length === 1) {
      return { glAccountId: hits[0]!.accountId, accountName: hits[0]!.accountName };
    }
  }
  return null;
}

/** 2dp for a money input — nobody types a statement balance to four places. */
function money2(value: string | null): string {
  if (value === null) return '';
  const n = parseFloat(value);
  return Number.isFinite(n) ? n.toFixed(2) : '';
}

export function StatementDropStart({
  companyId,
  accounts,
  onCancel,
}: {
  companyId: string;
  accounts: Account[];
  onCancel: () => void;
}) {
  const navigate = useNavigate();
  const parse = useParseStatement(companyId);
  const preview = useStatementPreview(companyId);
  const start = useStartReconFromStatement(companyId);
  // Brokerage statements only: the month's components and where each posts.
  const propose = useBrokerageProposal(companyId);
  const postBrokerage = usePostBrokerageStatement(companyId);
  const [proposal, setProposal] = useState<BrokerageProposal | null>(null);
  const [postLines, setPostLines] = useState<BrokerageProposedLine[]>([]);

  const [dragging, setDragging] = useState(false);
  const [fileName, setFileName] = useState<string | null>(null);
  const [readError, setReadError] = useState<string | null>(null);
  const [parsed, setParsed] = useState<ParsedStatement | null>(null);
  // The dropped file, kept so the final step can store it. The AI read it, and
  // the reconciliation it opens must carry it as evidence — a worksheet started
  // from a statement that was then thrown away left the month-end packet with
  // proof sheets and nothing behind them.
  const [statementFile, setStatementFile] = useState<{
    fileName: string;
    mimeType: string;
    content: string;
  } | null>(null);
  const [result, setResult] = useState<StatementReconPreview | null>(null);
  const [ticked, setTicked] = useState<Set<string>>(new Set());
  // Set when the account was read off the statement, so the UI can say so
  // rather than silently pre-selecting something.
  const [matchedAccountName, setMatchedAccountName] = useState<string | null>(null);
  const { data: bankData } = useBankAccounts(companyId);
  const bankAccounts = bankData?.bankAccounts ?? [];

  const [form, setForm] = useState({
    glAccountId: '',
    statementDate: '',
    periodFrom: '',
    beginningBalance: '',
    endingBalance: '',
  });

  const brokerage = parsed?.brokerage ?? null;

  // Once the account is known, ask which entries the statement calls for. Re-run
  // when the account changes: the posting map lives on the account.
  useEffect(() => {
    if (!brokerage || !form.glAccountId) {
      setProposal(null);
      setPostLines([]);
      return;
    }
    propose.mutate(
      { glAccountId: form.glAccountId, brokerage },
      {
        onSuccess: (p) => {
          setProposal(p);
          setPostLines(p.lines);
        },
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brokerage, form.glAccountId]);

  async function handleFile(file: File) {
    setReadError(null);
    setResult(null);
    setFileName(file.name);
    let content: string;
    try {
      content = await readAsBase64(file);
    } catch (err) {
      setReadError(err instanceof Error ? err.message : 'Could not read the file.');
      return;
    }
    const dropped = { fileName: file.name, mimeType: mimeOf(file), content };
    parse.mutate(
      dropped,
      {
        onSuccess: (p) => {
          setParsed(p);
          setStatementFile(dropped);
          const close = p.header.statementDate ?? p.header.periodTo ?? '';
          const hit = matchGlAccountFromStatement(p.header, file.name, bankAccounts);
          setMatchedAccountName(hit?.accountName ?? null);
          setForm((prev) => ({
            ...prev,
            // Read the account off the statement. When it cannot be read, leave
            // the picker EMPTY: this used to default to accounts[0], which is
            // whichever cash account sorts first, so the wrong account looked
            // like a deliberate choice and got clicked past. A required empty
            // field asks the question instead of answering it wrongly.
            glAccountId: hit?.glAccountId ?? prev.glAccountId,
            statementDate: close,
            periodFrom: p.header.periodFrom ?? '',
            beginningBalance: money2(p.header.beginningBalance),
            endingBalance: money2(p.header.endingBalance),
          }));
        },
      },
    );
  }

  function runPreview() {
    if (!parsed) return;
    const close = form.statementDate;
    preview.mutate(
      {
        glAccountId: form.glAccountId,
        parsed: {
          ...parsed,
          header: {
            ...parsed.header,
            statementDate: close || parsed.header.statementDate,
            periodFrom: form.periodFrom || parsed.header.periodFrom,
            periodTo: close || parsed.header.periodTo,
          },
        },
        ...(form.periodFrom ? { periodFrom: form.periodFrom } : {}),
        ...(close ? { periodTo: close } : {}),
      },
      {
        onSuccess: (p) => {
          setResult(p);
          // Pre-tick the confident ones only. Anything the model reached for, and
          // anything that had to stretch across dates, is left for the owner.
          setTicked(
            new Set(
              p.matches
                .filter((m) => m.confidence >= 0.9 && m.journalEntryId)
                .map((m) => m.journalEntryId as string),
            ),
          );
        },
      },
    );
  }

  const cleanMoney = (s: string) => s.replace(/[$,\s]/g, '');

  function openReconciliation(extraEntryIds: string[]) {
    start.mutate(
      {
        glAccountId: form.glAccountId,
        statementDate: form.statementDate,
        statementBeginningBalance: cleanMoney(form.beginningBalance) || '0',
        statementEndingBalance: cleanMoney(form.endingBalance),
        journalEntryIds: [...new Set([...ticked, ...extraEntryIds])],
        ...(statementFile ? { statement: statementFile } : {}),
      },
      {
        onSuccess: (recon) => {
          void navigate({ to: '/reconcile/$id', params: { id: recon.id } });
        },
      },
    );
  }

  // Brokerage: post the components first, then open the worksheet with those
  // entries (and the matched deposits) already ticked. A statement already
  // posted is not posted again; its entries are ticked instead.
  const linesToPost = postLines.filter((l) => parseFloat(l.amount) !== 0);
  const alreadyPostedIds = proposal?.alreadyPosted.map((p) => p.journalEntryId) ?? [];
  const brokerageReady =
    !brokerage ||
    alreadyPostedIds.length > 0 ||
    (proposal !== null && linesToPost.every((l) => !!l.accountId));

  function handleStart() {
    if (brokerage && alreadyPostedIds.length === 0 && linesToPost.length > 0) {
      postBrokerage.mutate(
        {
          glAccountId: form.glAccountId,
          statementDate: form.statementDate,
          institutionName: brokerage.header.institutionName,
          lines: linesToPost.map((l) => ({
            kind: l.kind,
            label: l.label.slice(0, 200),
            amount: l.amount,
            accountId: l.accountId as string,
            memo: l.memo.trim() || l.label,
          })),
          saveMap: true,
        },
        { onSuccess: (r) => openReconciliation(r.entries.map((e) => e.journalEntryId)) },
      );
      return;
    }
    openReconciliation(alreadyPostedIds);
  }

  const parseError = apiErrorMessage(parse.error);
  const previewError = apiErrorMessage(preview.error);
  const startError = apiErrorMessage(start.error) ?? apiErrorMessage(postBrokerage.error);

  const canPreview = !!parsed && !!form.glAccountId && !!form.statementDate;
  const canStart = canPreview && !!form.endingBalance.trim() && brokerageReady;
  const starting = start.isPending || postBrokerage.isPending;
  const startLabel = starting
    ? brokerage && postBrokerage.isPending
      ? 'Posting…'
      : 'Starting…'
    : brokerage && alreadyPostedIds.length === 0 && linesToPost.length > 0
      ? 'Post ' +
        linesToPost.length +
        ' entr' +
        (linesToPost.length === 1 ? 'y' : 'ies') +
        ' and start reconciliation'
      : ticked.size > 0
        ? 'Start with ' + ticked.size + ' ticked'
        : 'Start reconciliation';

  return (
    <div className="card" style={{ marginBottom: 16, padding: 16 }}>
      <h2 style={{ marginTop: 0, fontSize: 18 }}>Start from a statement</h2>
      <p className="muted" style={{ marginTop: 0, fontSize: 13 }}>
        Drop the PDF your bank sent. The closing date, the balances and the transactions are read
        from it, and the entries already in your books are matched against those rows. Nothing is
        saved until you press Start — every value below stays editable.
      </p>

      {!parsed && (
        <label
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragging(false);
            const file = e.dataTransfer.files?.[0];
            if (file) void handleFile(file);
          }}
          style={{
            display: 'block',
            border: '2px dashed ' + (dragging ? 'var(--accent, #2E86AB)' : 'var(--border)'),
            borderRadius: 8,
            padding: 28,
            textAlign: 'center',
            cursor: 'pointer',
            background: dragging ? 'var(--bg-surface-2)' : undefined,
          }}
        >
          <input
            type="file"
            accept={ACCEPTED}
            style={{ display: 'none' }}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void handleFile(file);
            }}
          />
          <div style={{ fontSize: 28, lineHeight: 1 }}>📄</div>
          <div style={{ marginTop: 8, fontWeight: 600 }}>
            {parse.isPending ? 'Reading the statement…' : 'Drop a bank statement PDF here'}
          </div>
          <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
            {parse.isPending ? fileName : 'or click to choose a file · PDF or CSV'}
          </div>
        </label>
      )}

      {readError && <div className="field-error" style={{ marginTop: 12 }}>{readError}</div>}
      {parseError && (
        <div className="field-error" style={{ marginTop: 12 }}>
          {parseError}
        </div>
      )}

      {parsed && (
        <>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 12,
              marginBottom: 12,
              flexWrap: 'wrap',
            }}
          >
            <div style={{ fontSize: 13 }}>
              Read <strong>{fileName}</strong>
              {parsed.header.institutionName ? ' · ' + parsed.header.institutionName : ''}
              {parsed.header.accountMask ? ' ····' + parsed.header.accountMask : ''}
              <span className="muted">
                {' '}
                ·{' '}
                {brokerage
                  ? 'brokerage statement · ' +
                    parsed.lines.length +
                    ' deposit' +
                    (parsed.lines.length === 1 ? '' : 's') +
                    '/withdrawal' +
                    (parsed.lines.length === 1 ? '' : 's')
                  : parsed.lines.length + ' transaction' + (parsed.lines.length === 1 ? '' : 's')}
              </span>
            </div>
            <button
              type="button"
              className="btn-secondary"
              style={{ fontSize: 12, padding: '4px 10px' }}
              onClick={() => {
                setParsed(null);
                setStatementFile(null);
                setResult(null);
                setFileName(null);
                parse.reset();
                preview.reset();
              }}
            >
              Use a different file
            </button>
          </div>

          {parsed.warning && (
            <div className="field-error" style={{ marginBottom: 12 }}>
              {parsed.warning}
            </div>
          )}

          <div className="form-grid">
            <label htmlFor="sds-acct">Account *</label>
            <div>
              <AccountPicker
                id="sds-acct"
                accounts={accounts}
                value={form.glAccountId}
                onChange={(id) => {
                  setForm((p) => ({ ...p, glAccountId: id }));
                  setMatchedAccountName(null);
                  setResult(null);
                }}
                required
              />
              {matchedAccountName && form.glAccountId ? (
                <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
                  Matched to <strong>{matchedAccountName}</strong> from the account number on the
                  statement. Change it if that is wrong.
                </div>
              ) : (
                !form.glAccountId && (
                  <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
                    The account number on this statement doesn't match a connected bank account —
                    choose the account yourself.
                  </div>
                )
              )}
            </div>

            <label htmlFor="sds-close">Statement date *</label>
            <div>
              <input
                id="sds-close"
                type="date"
                value={form.statementDate}
                onChange={(e) => {
                  setForm((p) => ({ ...p, statementDate: e.target.value }));
                  setResult(null);
                }}
                required
              />
              <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>
                Read from the statement. Correct it if the AI misread the closing date.
              </div>
            </div>

            <label htmlFor="sds-from">Period starts</label>
            <div>
              <input
                id="sds-from"
                type="date"
                value={form.periodFrom}
                onChange={(e) => {
                  setForm((p) => ({ ...p, periodFrom: e.target.value }));
                  setResult(null);
                }}
              />
              <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>
                Leave blank to search the month before the closing date.
              </div>
            </div>

            <label htmlFor="sds-beg">Beginning balance</label>
            <input
              id="sds-beg"
              type="text"
              inputMode="decimal"
              value={form.beginningBalance}
              onChange={(e) => setForm((p) => ({ ...p, beginningBalance: e.target.value }))}
              placeholder="0.00"
              style={{ textAlign: 'right', maxWidth: 160 }}
            />

            <label htmlFor="sds-end">Ending balance *</label>
            <input
              id="sds-end"
              type="text"
              inputMode="decimal"
              value={form.endingBalance}
              onChange={(e) => setForm((p) => ({ ...p, endingBalance: e.target.value }))}
              placeholder="0.00"
              style={{ textAlign: 'right', maxWidth: 160 }}
              required
            />
          </div>

          {brokerage && form.glAccountId && (
            <BrokeragePostingReview
              brokerage={brokerage}
              proposal={proposal}
              lines={postLines}
              accounts={accounts}
              glAccountId={form.glAccountId}
              onChange={setPostLines}
              loading={propose.isPending}
            />
          )}
          {brokerage && apiErrorMessage(propose.error) && (
            <div className="field-error" style={{ marginTop: 12 }}>
              {apiErrorMessage(propose.error)}
            </div>
          )}

          {previewError && (
            <div className="field-error" style={{ marginTop: 12 }}>
              {previewError}
            </div>
          )}

          <div style={{ display: 'flex', gap: 8, marginTop: 16, flexWrap: 'wrap' }}>
            <button
              type="button"
              className="btn-secondary"
              disabled={!canPreview || preview.isPending}
              onClick={runPreview}
            >
              {preview.isPending
                ? 'Matching…'
                : result
                  ? 'Match again'
                  : brokerage
                    ? 'Match deposits against my books'
                    : 'Match against my books'}
            </button>
            <button
              type="button"
              className="btn-primary"
              disabled={!canStart || starting}
              onClick={handleStart}
              title={
                result
                  ? undefined
                  : brokerage
                    ? 'Match against my books first to tick the deposits the bank feed already posted.'
                    : 'You can start without matching — the worksheet will simply begin empty.'
              }
            >
              {startLabel}
            </button>
            <button type="button" className="btn-secondary" onClick={onCancel}>
              Cancel
            </button>
          </div>

          {startError && <div className="field-error" style={{ marginTop: 12 }}>{startError}</div>}

          {result && (
            <MatchReview
              result={result}
              ticked={ticked}
              onToggle={(entryId) =>
                setTicked((prev) => {
                  const next = new Set(prev);
                  if (next.has(entryId)) next.delete(entryId);
                  else next.add(entryId);
                  return next;
                })
              }
            />
          )}
        </>
      )}
    </div>
  );
}

function MatchReview({
  result,
  ticked,
  onToggle,
}: {
  result: StatementReconPreview;
  ticked: Set<string>;
  onToggle: (entryId: string) => void;
}) {
  /**
   * Candidates are per bank-side LINE, so several share one entry id. Clearing
   * is per ENTRY, so roll the lines up: keep the first line for its date and
   * description, and sum the movement so the amount shown is what ticking the
   * entry will actually contribute to the cleared balance.
   */
  const candidateById = useMemo(() => {
    const byEntry = new Map<string, (typeof result.candidates)[number]>();
    for (const c of result.candidates) {
      const existing = byEntry.get(c.journalEntryId);
      if (!existing) {
        byEntry.set(c.journalEntryId, { ...c });
      } else {
        existing.signedMovement = (
          parseFloat(existing.signedMovement) + parseFloat(c.signedMovement)
        ).toFixed(4);
      }
    }
    return byEntry;
  }, [result.candidates]);

  const partiallyMatched = result.partiallyMatchedEntryIds ?? [];

  const followOnCount = result.matches.filter((m) => m.outsidePeriod).length;

  return (
    <div style={{ marginTop: 20 }}>
      <div
        style={{
          display: 'flex',
          gap: 16,
          alignItems: 'baseline',
          flexWrap: 'wrap',
          marginBottom: 8,
        }}
      >
        <h3 style={{ fontSize: 15, margin: 0 }}>
          {result.matches.length} of {result.parsed.lines.length} statement rows matched
        </h3>
        <span className="muted" style={{ fontSize: 12 }}>
          Searched {formatAccountingDate(result.periodFrom)} – {formatAccountingDate(result.periodTo)}
          , plus a grace window through {formatAccountingDate(result.followOnThrough)}.
          {result.aiUsed
            ? ' Exact matches were found first; the AI was asked only about what was left.'
            : ' Every match was exact — no AI was needed.'}
        </span>
      </div>

      {followOnCount > 0 && (
        <p style={{ fontSize: 13, margin: '0 0 10px' }}>
          <span className="tag warn">{followOnCount} outside the period</span>{' '}
          <span className="muted">
            Dated after the statement closed. Usually a late post; sometimes an entry with the
            wrong date on it. Worth a look before you tick them.
          </span>
        </p>
      )}

      <table className="table">
        <thead>
          <tr>
            <th style={{ width: 34 }}></th>
            <th>Statement row</th>
            <th style={{ textAlign: 'right' }}>Amount</th>
            <th>Matched entry</th>
            <th style={{ width: 80 }}>Confidence</th>
          </tr>
        </thead>
        <tbody>
          {result.parsed.lines.map((line, index) => {
            const match = result.matches.find((m) => m.statementLineIndex === index);
            const entryId = match?.journalEntryId ?? null;
            const checked = entryId ? ticked.has(entryId) : false;
            const candidate = entryId ? candidateById.get(entryId) : undefined;
            return (
              <tr key={index}>
                <td>
                  {entryId ? (
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => onToggle(entryId)}
                      aria-label={'Clear ' + (match?.entryNumber ?? '')}
                    />
                  ) : null}
                </td>
                <td>
                  {line.description || <span className="muted">(no description)</span>}
                  <div className="muted" style={{ fontSize: 12 }}>
                    {formatAccountingDate(line.date)}
                  </div>
                </td>
                <td style={{ textAlign: 'right' }}>{formatCurrency(line.amount, true)}</td>
                <td>
                  {match ? (
                    <>
                      <span style={{ fontFamily: 'ui-monospace, monospace', fontSize: 13 }}>
                        {match.entryNumber}
                      </span>{' '}
                      {match.outsidePeriod && (
                        <span className="tag warn" style={{ fontSize: 11 }}>
                          outside period
                        </span>
                      )}
                      <div className="muted" style={{ fontSize: 12 }}>
                        {match.entryDate ? formatAccountingDate(match.entryDate) : ''}
                        {candidate ? ' · ' + candidate.description : ''}
                      </div>
                      <div className="muted" style={{ fontSize: 11 }}>{match.reason}</div>
                    </>
                  ) : (
                    <span className="muted">
                      Nothing in the books explains this row — you will need to post it.
                    </span>
                  )}
                </td>
                <td>
                  {match ? (
                    <span
                      className={
                        match.confidence >= 0.9 ? 'tag ok' : match.confidence >= 0.75 ? 'tag' : 'tag warn'
                      }
                      style={{ fontSize: 11 }}
                    >
                      {Math.round(match.confidence * 100)}%
                    </span>
                  ) : null}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* A split entry only partly evidenced by the statement. Clearing is
        * entry-level, so ticking one of these counts its WHOLE movement against
        * a statement that showed only part — which is a question worth asking,
        * not a match to pre-tick. */}
      {partiallyMatched.length > 0 && (
        <div className="card accent-risk" style={{ marginTop: 12, padding: 12, fontSize: 13 }}>
          <strong>
            {partiallyMatched.length}{' '}
            {partiallyMatched.length === 1 ? 'entry has' : 'entries have'} only part of their bank
            activity on this statement
          </strong>
          <p className="muted" style={{ margin: '6px 0 0' }}>
            These post to the bank account on more than one line, and the statement accounts for
            some but not all of them. Clearing one counts its full amount, so check the rest before
            ticking it.
          </p>
          <ul style={{ margin: '8px 0 0', paddingLeft: 18 }}>
            {partiallyMatched.map((id) => {
              const c = candidateById.get(id);
              if (!c) return null;
              return (
                <li key={id}>
                  <span style={{ fontFamily: 'ui-monospace, monospace' }}>{c.entryNumber}</span> ·{' '}
                  {formatAccountingDate(c.date)} · total {formatCurrency(c.signedMovement, true)}{' '}
                  <span className="muted">({c.entryLineCount} bank lines)</span>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {result.unmatchedEntryIds.length > 0 && (
        <details style={{ marginTop: 10, fontSize: 13 }}>
          <summary className="muted">
            {result.unmatchedEntryIds.length} unreconciled entr
            {result.unmatchedEntryIds.length === 1 ? 'y' : 'ies'} in this window that no statement
            row claimed (outstanding cheques and deposits in transit look like this)
          </summary>
          <table className="table" style={{ marginTop: 6 }}>
            <tbody>
              {result.unmatchedEntryIds.map((id) => {
                const c = candidateById.get(id);
                if (!c) return null;
                return (
                  <tr key={id}>
                    <td style={{ width: 34 }}>
                      <input
                        type="checkbox"
                        checked={ticked.has(id)}
                        onChange={() => onToggle(id)}
                        aria-label={'Clear ' + c.entryNumber}
                      />
                    </td>
                    <td>
                      <span style={{ fontFamily: 'ui-monospace, monospace', fontSize: 13 }}>
                        {c.entryNumber}
                      </span>{' '}
                      {c.outsidePeriod && (
                        <span className="tag warn" style={{ fontSize: 11 }}>
                          outside period
                        </span>
                      )}
                      <div className="muted" style={{ fontSize: 12 }}>
                        {formatAccountingDate(c.date)} · {c.description}
                      </div>
                    </td>
                    <td style={{ textAlign: 'right' }}>{formatCurrency(c.signedMovement, true)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </details>
      )}
    </div>
  );
}
